
import { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { UserProfile, TeamMembership } from '../types';
import { AuthService } from '../services/AuthService';
import { TeamService } from '../services/TeamService';
import { UserService } from '../services/UserService';

interface ProfileManagementProps {
    user: User | null;
    setLoading: (loading: boolean) => void;
    setIsSettling: (settling: boolean) => void;
}

export const useProfileManagement = ({ user, setLoading, setIsSettling }: ProfileManagementProps) => {
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

    // Users Profile Listener & Migration
    useEffect(() => {
        if (!user) {
            setUserProfile(null);
            setIsSettling(false); // FORCED RESET
            setLoading(false);    // FORCED RESET
            return;
        }

        return UserService.listenToUserProfile(user.uid, async (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                const profile = { id: docSnap.id, ...data } as UserProfile;

                // --- MIGRATION Logic ---
                if (profile.teamId && (!profile.teams || profile.teams.length === 0)) {
                    console.log("Migrating user to multi-team structure...");
                    setIsSettling(true);
                    let teamName = 'My Team';
                    try {
                        const teamDoc = await TeamService.getTeam(profile.teamId);
                        if (teamDoc) teamName = teamDoc.name;
                    } catch (e) { console.error("Migration fetch error", e); }

                    const newTeam: TeamMembership = {
                        teamId: profile.teamId,
                        teamName: teamName,
                        role: profile.role || 'engineer'
                    };

                    await UserService.migrateUserProfile(user.uid, {
                        teams: [newTeam],
                        teamIds: [profile.teamId],
                        activeTeamId: profile.teamId,
                        activeTeamIds: [profile.teamId]
                    });
                    return;
                }

                if (profile.activeTeamId && (!profile.activeTeamIds || profile.activeTeamIds.length === 0)) {
                    console.log("Migrating activeTeamId to activeTeamIds...");
                    setIsSettling(true);
                    await UserService.migrateUserProfile(user.uid, { activeTeamIds: [profile.activeTeamId] });
                    return;
                }

                // --- NEW: Multi-Org Migration ---
                if (profile.orgId && (!profile.orgIds || profile.orgIds.length === 0)) {
                    console.log("[Migration] Migrating single orgId to orgIds array...");
                    setIsSettling(true);
                    await UserService.migrateUserProfile(user.uid, { orgIds: [profile.orgId] });
                    return;
                }

                setUserProfile(profile);
                
                // Only stop settling if we have a valid profile AND an OrgId
                if (profile && profile.orgId) {
                    setIsSettling(false);
                }
            } else {
                setUserProfile(null);
                // If doc doesn't exist, handleUserSync is likely creating it.
                // CHARACTER-FOR-CHARACTER Logic Restoration:
                await AuthService.handleUserSync(user);
            }
            setLoading(false); 
        });
    }, [user]);

    return {
        userProfile,
        setUserProfile
    };
};
